import { Component, Input, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { catchError, forkJoin, lastValueFrom, map, Observable, of } from 'rxjs';
import { isEqualWith } from 'lodash-es';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { OpOrderModel, ThermalOpOrderModel } from '../../models/op-order.model';
import { BaseOperation, EMPTY_GUID, OperationType } from '../../models/thermal-operation.model';
import { ThermalOperationsService } from '../../services/thermal-operations.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { User, UserRoles } from 'src/app/core/components/user-admin-page/user-model';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { CdkDrag, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { WellTypeService } from 'src/app/wellbore-inputs/services/well-type-datums.service';
import { WellType } from 'src/app/shared/models/wellType.model';
import { OperationDataComponent } from './operation-data/operation-data.component';

@Component({
  selector: 'app-thermal-operations',
  templateUrl: './thermal-operations.component.html',
  providers: [ConfirmationService, CdkDrag],
  standalone: false
})
export class ThermalOperationsComponent implements OnInit, OnDestroy {
  constructor(
    private _thermalOperationsService: ThermalOperationsService,
    private _confirmationService: ConfirmationService,
    private _signalRService: SignalRService,
    private _store: StoreService,
    private _messenger: MediatorService,
    private _toaster: AppNotificationService,
    private _wellType: WellTypeService
  ) { }

  public operations: Partial<BaseOperation>[];
  public previousOperations: Partial<BaseOperation>[];
  public selectedOperation: Partial<BaseOperation>[] = [];
  public selectedOperationDetail$: Observable<BaseOperation>;
  public isLoading = signal<boolean>(false);
  public isSelectingOperation = signal(false);
  @ViewChild(OperationDataComponent)
  private _operationData: OperationDataComponent;
  public drawerOpen: boolean;
  public userUnits: UserUnitsModel;
  public userRoles : UserRoles;
  public user: User;
  public wellType: WellType;

  private _previousSelectedOperationId: string = EMPTY_GUID;
  private _operationSelectActive: boolean;
  private _selectedFluidId: string | null;
  private _disableaddOperation = false;
  private _pendingSave: Promise<boolean> = Promise.resolve(true);
  private _createdOperationId: string | null = null;
  private _lastSavedOperation: BaseOperation | null = null;

  // State
  @Input()
  private componentId: string;

  async ngOnInit(): Promise<void> {

    this.isLoading.set(true);
    this.drawerOpen = true;

    this.user = await this._store.get<User>(StorageKeys.USER);
    this.userUnits = await this._store.get<UserUnitsModel>(StorageKeys.UNITS);
    this.userRoles = this.user.roles;

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    this._operationSelectActive = true;
    this.getThermalOperations();
  }

  private signalRfunc(data: any) {
    if (data.action === PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this.isLoading.set(true);
      this._operationSelectActive = true;
      this.getThermalOperations();
    }
  }

  async getThermalOperations(): Promise<void> {
    try {
      const [operations, wellType] = await Promise.all([
        lastValueFrom(this._thermalOperationsService.getThermalOperations()),
        lastValueFrom(this._wellType.getWellType())
      ]);
      this.operations = operations?.length > 0 ? operations : [];
      this.selectedOperation = this.operations?.length > 0 ? [this.operations[0]] : [];
      this._previousSelectedOperationId = this.selectedOperation[0]?.id;
      this.wellType = wellType;
  
      if (this.selectedOperation.length > 0) {
        const [operation, previousOperations] = await Promise.all([
          lastValueFrom(this._thermalOperationsService.getThermalOperationById(this.selectedOperation[0]?.id)),
          lastValueFrom(this._thermalOperationsService.getPreviousThermalOperations(this.selectedOperation[0]?.id))
        ]);
        this._selectedFluidId = operation.inside['fluid']?.id ?? null;
        this.previousOperations = previousOperations;
  
        this.selectedOperationDetail$ = new Observable(observer => observer.next(operation));
        this._lastSavedOperation = structuredClone(operation);
        this._operationSelectActive = false;
      } else {
        this.previousOperations = await lastValueFrom(this._thermalOperationsService.getPreviousThermalOperations('00000000-0000-0000-0000-000000000000'));
        this.clearOperationDetails();
      }
    } catch (error) {
      console.log(error);
    } finally {
      this.isLoading.set(false);
    }
  }

  async onOperationSelect(e, calledFromCode = false, isDelete = false): Promise<void> {
    if (!e.value || this.isSelectingOperation()) {
      return;
    }
    const value = e.value[0] ?? e.value;

    if (this.selectedOperation[0]?.id === value.id && !calledFromCode) {
      return;
    }

    if (calledFromCode == true && !isDelete) {
      this.isLoading.set(false);
      return;
    }
  
    this.isSelectingOperation.set(true);
    try {
      if (!calledFromCode && !this.userRoles?.readOnly) {
        const currentOperation = this._operationData?.getOperationForSave();
        const pendingSaveSucceeded = await this._pendingSave;
        if (!pendingSaveSucceeded) {
          this._pendingSave = Promise.resolve(true);
          return;
        }
        if (this._operationData && !currentOperation) {
          this._toaster.showError('Please double check the current operation inputs before selecting another operation.');
          return;
        }
        if (currentOperation) {
          const operationForSave = currentOperation.id === EMPTY_GUID && this._createdOperationId
            ? { ...currentOperation, id: this._createdOperationId }
            : currentOperation;
          if (!this.areThermalOperationsEqual(operationForSave, this._lastSavedOperation)
            && !await this.queueOperationSave(operationForSave)) {
            this._pendingSave = Promise.resolve(true);
            return;
          }
        } else if (this.operations?.some(operation => operation.id === EMPTY_GUID)) {
          this._toaster.showError('The current operation could not be saved. Please double check inputs.');
          return;
        }
      }

      this.isLoading.set(true);
      this._operationSelectActive = false;
      this.clearOperationDetails();
      this.previousOperations = [];
      const selectedOpId = value.id;
      if (selectedOpId !== EMPTY_GUID) {
        const [operation, previousOperations] = await lastValueFrom(forkJoin(this.currentAndPreviousOperationsSources(selectedOpId)));
  
        this._selectedFluidId = operation.inside['fluid']?.id ?? null;
        this.selectedOperation = [this.operations.find(x => x.id === selectedOpId)];
        this.selectedOperationDetail$ = new Observable(observer => observer.next(operation));
        this.previousOperations = previousOperations;
        this._previousSelectedOperationId = this.selectedOperation[0]?.id;
        this._lastSavedOperation = structuredClone(operation);
        this.isLoading.set(false);
      } else {
        this.selectedOperationDetail$ = new Observable(observer => observer.next(e.value));
        this.isLoading.set(false);
      }
    } catch (err) {
      this.isLoading.set(false);
      console.log(err);
    } finally{
      if (!calledFromCode && this.selectedOperation[0] && this.selectedOperation[0].id !== value.id) {
        this.selectedOperation = this.selectedOperation.map(operation => ({ ...operation }));
      }
      this.isLoading.set(false);
      this.isSelectingOperation.set(false);
    }
  }

  drawerToggle() {
    this.drawerOpen = !this.drawerOpen;
  }

  updateOrCreateOperation(operation: BaseOperation): void {
    if (this.isSelectingOperation() || !operation?.inside) {
      return;
    }
    if(this._operationSelectActive || ((operation.inside.type == 'shutIn' || operation.inside.type == 'screenout') && operation.annulus == null)){
      this._operationSelectActive = false;
      return;
    }
    
    if ((operation.inside.type == 'production' || operation.inside.type == 'injection') && operation.annulus == null) {
      return;
    }
    
    void this.queueOperationSave(operation);
  }

  private areThermalOperationsEqual(
    operation: BaseOperation,
    savedOperation: BaseOperation | null
  ): boolean {
    const fluidReferenceKeys = new Set(['fluid', 'drillingFluid', 'leadSlurry', 'slurry']);
    const hasReferenceId = (value: unknown): value is { readonly id: string } =>
      typeof value === 'object' && value !== null && 'id' in value
      && typeof value.id === 'string' && value.id.length > 0;

    return isEqualWith(operation, savedOperation, (current: unknown, saved: unknown, key?: string | number) => {
      // The form hydrates fluid references with catalog data that is saved separately.
      if (typeof key === 'string' && fluidReferenceKeys.has(key)
        && hasReferenceId(current) && hasReferenceId(saved)) {
        return current.id === saved.id;
      }
      return undefined;
    });
  }

  private queueOperationSave(operation: BaseOperation): Promise<boolean> {
    const snapshot = structuredClone(operation);
    this._pendingSave = this._pendingSave.then(() => this.saveOperation(snapshot));
    return this._pendingSave;
  }

  private async saveOperation(operation: BaseOperation): Promise<boolean> {
    if (!operation.inside) {
      this._toaster.showError('Please specify the current operation inputs before selecting another operation.');
      return false;
    }
    if (operation.id === EMPTY_GUID && this._createdOperationId) {
      operation = { ...operation, id: this._createdOperationId };
    }
    try {
      if (operation.inside['flow'] && !operation.inside['flow']['type'] && (operation.inside.type === OperationType.DRILLING
        || operation.inside.type === OperationType.CEMENTING
        || operation.inside.type === OperationType.CIRCULATION
        || operation.inside.type === OperationType.RUNCASINGANDCIRCULATE
        || operation.inside.type === OperationType.TRIPPIPEANDCIRCULATE)) {
          operation.inside['flow'].type = 'standardFluidFlow';
        }
      if (this.areThermalOperationsEqual(operation, this._lastSavedOperation)) {
        return true;
      }
      const originalId = operation.id;
      const isNew = originalId === EMPTY_GUID;
      if (isNew) {
        const id = await lastValueFrom(this._thermalOperationsService.createThermalOperation(operation));
        this._createdOperationId = id;
        operation = { ...operation, id };
      } else {
        await lastValueFrom(this._thermalOperationsService.updateThermalOperation(operation));
      }
      this.operations = this.operations.map(item => item.id === originalId
        ? { ...item, id: operation.id, name: operation.name }
        : item);
      const fluidId = operation.inside && 'fluid' in operation.inside ? operation.inside.fluid?.id ?? null : null;
      if (isNew || (this._selectedFluidId != null && this._selectedFluidId !== fluidId)) {
        this.selectedOperationDetail$ = of(operation);
      }
      this.selectedOperation = [this.operations.find(item => item.id === operation.id)];
      this._previousSelectedOperationId = operation.id;
      this._selectedFluidId = fluidId;
      this._lastSavedOperation = structuredClone(operation);
      this._messenger.publish(new DataSavedMessage('Thermal Operations'));
      return true;
    } catch (error) {
      console.error('Failed to save thermal operation', error);
      this._toaster.showError(`Operation "${operation.name}" could not be saved. Please double check inputs.`);
      return false;
    }
  }

  deleteOperation(operation: BaseOperation): void {
    if (this.isSelectingOperation()) {
      return;
    }
    if (operation.id === EMPTY_GUID) {
      this.selectOperationAfterDelete(operation.id);
      return;
    }
    this._confirmationService.confirm({
          message: 'Are you sure that you want to delete operation ' + operation.name.bold() + '?',
          accept: () => {
        this._thermalOperationsService.deleteThermalOperation(operation.id).subscribe(() => {
          this._operationSelectActive = true;
          this.selectOperationAfterDelete(operation.id);
          this._messenger.publish(new DataSavedMessage('Thermal Operations'));
        });
      }
    });
  }

  private selectOperationAfterDelete(operationId) {
    this.isLoading.set(true);
    const index = this.operations.indexOf(this.operations.find(o => o.id === operationId));
    this.operations.splice(index, 1);
    this.operations = [...this.operations];
    this.selectedOperation = this.operations.length !== 0 ? [this.operations[0]] : null;
    if (this.selectedOperation !== null) {
      this._thermalOperationsService.getThermalOperationById(this.selectedOperation[0].id).subscribe({
        next: operation => {
          this.onOperationSelect({value: [operation]}, true, true);
        },
        error: err => {
          console.log(err);
          this.isLoading.set(false);
        }
      });
    } else {
      this.clearOperationDetails();
      this.isLoading.set(false);
    }
  }

  cloneOperation(operation: BaseOperation): void {
    if (this.isSelectingOperation()) {
      return;
    }
    const existingCopies = this.operations.filter(op => 
        op.name.startsWith(operation.name + ' - Copy') ||
        op.name === operation.name + ' - Copy'
    );
    
    let copyNumber = 1;
    const copyRegex = / - Copy (\d+)$/;
    
    existingCopies.forEach(op => {
        const match = op.name.match(copyRegex);
        if (match) {
          const currentNum = parseInt(match[1], 10);
          copyNumber = Math.max(copyNumber, currentNum + 1);
        } else if (op.name === operation.name + ' - Copy') {
          // This handles the case where there's just "Copy"
          copyNumber = Math.max(copyNumber, 2);
        }
    });
    
    const operationName = `${operation.name} - Copy ${copyNumber}`;
    
    this.selectedOperationDetail$ = new Observable(observer => observer.next(null));
    this._thermalOperationsService.cloneThermalOperation(operation.id, operationName).subscribe(res => {
        this._messenger.publish(new DataSavedMessage('Thermal Operations'));
        forkJoin(this.currentAndPreviousOperationsSources(res.id)).pipe(
            map(([clonedOperation, previousOperations]) => {
                this.operations = [...this.operations, clonedOperation];
                this.selectedOperation = [this.operations.find(o => o.id === clonedOperation.id)];
                this.selectedOperationDetail$ = new Observable(observer => observer.next(clonedOperation));
                this.previousOperations = previousOperations;
                this._previousSelectedOperationId = clonedOperation.id;
                this.onOperationSelect({value: [clonedOperation]}, true, true);
                this.onReorder();
            }),
            catchError(err => {
                this.isLoading.set(false);
                return err;
            })
        ).subscribe();
    });
  }

  private currentAndPreviousOperationsSources(operationId: string): [Observable<BaseOperation>, Observable<Partial<BaseOperation>[]>] {
    return [
      this._thermalOperationsService.getThermalOperationById(operationId) as Observable<any>,
      this._thermalOperationsService.getPreviousThermalOperations(operationId) as Observable<any>
    ];
  }

  addOperation(): void {
    if (this.isSelectingOperation()) {
      return;
    }
    if (this.operations.length > 0 && this.operations?.find(o => o.id === EMPTY_GUID) !== undefined || this._disableaddOperation) {
      return;
    }
    this.isLoading.set(true);
    this._disableaddOperation = true;
    this._thermalOperationsService.getPreviousThermalOperations('00000000-0000-0000-0000-000000000000').subscribe(previousOps => {
      this.previousOperations = previousOps;
      this._createdOperationId = null;
      this._lastSavedOperation = null;
      this._operationSelectActive = false;
      const newOperation: BaseOperation = {
        id: EMPTY_GUID,
        name: 'New Operation',
        time: { value: 24, unit: 'DurationUnit.Hour'},
        inside: null,
        annulus: null,
        previousOperationId: '00000000-0000-0000-0000-000000000000',
        volume: {
          volumeSelected: false,
          volumeSelection: null,
          volumeValue: 0
        },
        ambientConditions: {
          airVelocitySurface: this.userUnits.speed == 'ft/s' ? 10 : 3,
          seaCurrentVelocity: this.userUnits.speed == 'ft/s' ? 3 : 1,
        }
      };
      this.operations = [...this.operations, newOperation];
      this.selectedOperation = this.operations.length > 0 ? [this.operations.find(o => o.id === newOperation.id)] : [];
      this.selectedOperationDetail$ = new Observable(observer => observer.next(newOperation));
      this._disableaddOperation = false;
      this.isLoading.set(false);
    });
  }

  clearOperationDetails(): void {
    const clearedOperation: BaseOperation = {
      id: null,
      name: null,
      time: { value: null, unit: 'DurationUnit.Hour'},
      inside: null,
      annulus: null,
      previousOperationId: null,
      volume: {
        volumeSelected: false,
        volumeSelection: null,
        volumeValue: 0
      },
      ambientConditions: {
        airVelocitySurface: null,
         seaCurrentVelocity: null
      }
    };
    this.selectedOperation = [this.operations.find(o => o.id === clearedOperation.id)];
    this.selectedOperationDetail$ = new Observable(observer => observer.next(clearedOperation));
  }

  drop(event: CdkDragDrop<string[]>) {
    if (this.isSelectingOperation()) {
      return;
    }
    moveItemInArray(this.operations, event.previousIndex, event.currentIndex);
    this.onReorder();
  }

  async onReorder() {
    const order = this.operations.map((o,i)=> new OpOrderModel({opId: o.id,  sort: i }))
    const sortModel = new ThermalOpOrderModel({sortOrder :order});
    await lastValueFrom(this._thermalOperationsService.updateThermalOperationOrder(sortModel));
  }

  ngOnDestroy() {
    this.signalRfunc = null;
  }
}
